import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { endOfDay, parse, startOfDay, isValid, format } from 'date-fns';
import { isEqual } from 'lodash-es';

import { ControlError } from '../../models';

@Component({
  selector: 'kt-date-input',
  templateUrl: './date-input.component.html',
  styleUrls: ['./date-input.component.scss'],
})
export class DateInputComponent implements OnInit, OnDestroy {
  private subscriptions = new Subscription();
  private _control: FormControl;
  private syncing: boolean;
  dateControl: FormControl;
  required: boolean;
  placeholder: string;
  @ViewChild('input') input: ElementRef;
  @Input() isRange: boolean; // type: date
  @Input() readonly: boolean;
  @Input() id: string;
  @Input() type: 'date' | 'time' | 'datetime' = 'date';
  @Input() min: string; // type: date, or datetime [format: yyyy-MM-dd]
  @Input() max: string; // type: date, or datetime [format: yyyy-MM-dd]
  @Input() format: string;
  @Input() label: string;
  @Input() size: 'sm' | 'md' | 'lg' = 'md';
  @Input() color: 'primary' | 'secondary' | 'tertiary' | 'neutral' = 'neutral';
  @Input() errors: ControlError[] = [];
  @Input()
  set control(control: FormControl) {
    if (!control) return;

    this._control = control;
    this.dateControl = new FormControl({ value: control.value, disabled: control.disabled });
    this.required = false;

    if (control.validator) {
      const validator = control.validator({} as any);
      this.required = !!validator?.required;
    }
  }
  get control(): FormControl {
    return this._control;
  }
  @Output() focused = new EventEmitter();
  @Output() blurred = new EventEmitter();

  ngOnInit(): void {
    this.isRange = this.isRange && this.type === 'date';

    this.placeholder =
      this.type === 'date'
        ? this.isRange
          ? 'M/d/yyyy - M/d/yyyy'
          : 'M/d/yyyy'
        : this.type === 'time'
        ? 'h:mm a'
        : 'M/d/yyyy, h:mm a';

    this.errors = this.errors.map((e) => {
      if (e.validation === 'required') {
        return { ...e, validation: this.isRange ? 'owlRequiredDateTimeRange' : 'required' };
      }

      if (e.validation === 'pattern') {
        return { ...e, validation: this.isRange ? 'owlDateTimeRange' : 'owlDateTimeParse' };
      }

      return e;
    });

    this.normalizeMinMax();

    this.subscriptions.add(
      this.control.valueChanges
        .pipe(distinctUntilChanged())
        .subscribe((inVal) => this.syncValFromInputCtrl(inVal)),
    );

    this.subscriptions.add(
      this.control.statusChanges
        .pipe(distinctUntilChanged())
        .subscribe(() => this.syncStatusFromInputCtrl()),
    );

    this.subscriptions.add(
      this.dateControl.valueChanges
        .pipe(
          debounceTime(500),
          distinctUntilChanged(),
          map((val) => this.formatDateOutput(val)),
        )
        .subscribe((outVal) => this.syncValFromDateCtrl(outVal)),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private normalizeMinMax(): void {
    if (this.type === 'time') {
      this.min = this.max = undefined;
    } else {
      ['min', 'max'].forEach((key) => {
        let date = this[key];

        if (!date) return;

        date = new Date(date);

        if (isNaN(+date)) return;

        this[key] = key === 'min' ? startOfDay(date) : endOfDay(date);
      });
    }
  }

  private syncValFromInputCtrl(value: any): void {
    if (this.syncing) return;

    const current = this.dateControl.value;

    if (isEqual(current, value)) return;

    this.syncing = true;
    this.dateControl.markAsTouched();
    this.dateControl.setValue(value);
    this.syncing = false;
  }

  private syncStatusFromInputCtrl(): void {
    if (this.control.disabled && !this.dateControl.disabled) {
      this.dateControl.disable({ emitEvent: false });
    } else if (!this.control.disabled && this.dateControl.disabled) {
      this.dateControl.enable({ emitEvent: false });
    }
  }

  private formatDateOutput(value: any): string {
    if (!value) return null;

    const dates = this.isRange ? value : [value];
    const formatted = this.format
      ? dates.filter((d) => !!d).map((d) => format(d, this.format) || '')
      : dates;

    if (!formatted.length) return null;

    return this.isRange ? formatted : formatted[0];
  }

  private matchFormat(dateString: string, validFormat: string): boolean {
    const parsed = parse(dateString, validFormat, new Date());

    return isValid(parsed) && format(parsed, validFormat) === dateString;
  }

  private syncValFromDateCtrl(value: any): void {
    if (this.syncing) return;

    const current = this.control.value;
    const str = this.input?.nativeElement?.value;

    if (isEqual(current, value)) return;

    if (!this.isRange && str && !this.matchFormat(str, this.placeholder)) {
      this.dateControl.setErrors({ owlDateTimeParse: true });
    }

    if (this.dateControl.invalid) {
      value = null;
    }

    this.syncing = true;
    this.control.setValue(value);
    this.syncing = false;
  }
}
